import { useState, useEffect } from "react";
import { atualizarEntrega } from "services/entrega.service";
import { getPerspectivasECBB, PerspectivaECBB } from "services/category.service";

interface ApprovalLogEntry {
  action: "aprovada" | "reprovada" | "aguardando" | "editada";
  user_id: string;
  user_name: string;
  user_matricula: string;
  timestamp: string;
}

interface ModalEditarIniciativaProps {
  entrega: any;
  onSave: (entregaAtualizada: any) => void;
  onClose: () => void;
  currentUser?: any;
}

export function ModalEditarIniciativa({
  entrega,
  onSave,
  onClose,
  currentUser,
}: ModalEditarIniciativaProps) {
  const [title, setTitle] = useState(entrega.title || "");
  const [description, setDescription] = useState(entrega.description || "");
  const [result, setResult] = useState(entrega.result || "");
  const [categoria, setCategoria] = useState(entrega.indicator?.perspectiva || "");
  const [indicadorNome, setIndicadorNome] = useState(entrega.indicator?.nome || "");
  const [saving, setSaving] = useState(false);

  const [perspectivasECBB, setPerspectivasECBB] = useState<PerspectivaECBB[]>([]);
  const [perspectivaSelecionada, setPerspectivaSelecionada] = useState<PerspectivaECBB | null>(null);

  // Datas
  const formatDateForInput = (dateValue: any): string => {
    if (!dateValue) return "";
    if (dateValue.$date) return new Date(dateValue.$date).toISOString().split("T")[0];
    if (typeof dateValue === "string") return new Date(dateValue).toISOString().split("T")[0];
    if (typeof dateValue === "number") return new Date(dateValue).toISOString().split("T")[0];
    return "";
  };

  const [inicioEntrega, setInicioEntrega] = useState(formatDateForInput(entrega.start));
  const [conclusaoEntrega, setConclusaoEntrega] = useState(formatDateForInput(entrega.end));

  // Validação
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const carregar = async () => {
      try {
        const perspectivas = await getPerspectivasECBB();
        setPerspectivasECBB(perspectivas);

        if (categoria) {
          const found = perspectivas.find((p) => p.title === categoria);
          setPerspectivaSelecionada(found || null);
        }
      } catch (error) {
        console.error("Erro ao carregar perspectivas:", error);
      }
    };
    carregar();
  }, []);

  useEffect(() => {
    if (categoria) {
      const perspectiva = perspectivasECBB.find((p) => p.title === categoria);
      setPerspectivaSelecionada(perspectiva || null);
    } else {
      setPerspectivaSelecionada(null);
    }
  }, [categoria, perspectivasECBB]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) newErrors.title = "Nome da iniciativa é obrigatório";
    if (title.length > 250) newErrors.title = "Máximo de 250 caracteres";
    if (!categoria) newErrors.categoria = "Categoria é obrigatória";
    if (!inicioEntrega) newErrors.inicioEntrega = "Data de início é obrigatória";
    if (!conclusaoEntrega) newErrors.conclusaoEntrega = "Data de conclusão é obrigatória";

    if (inicioEntrega) {
      const inicio = new Date(inicioEntrega);
      if (inicio.getFullYear() !== 2025) newErrors.inicioEntrega = "A data de início deve ser do ano de 2025";
    }
    if (conclusaoEntrega) {
      const conclusao = new Date(conclusaoEntrega);
      if (conclusao.getFullYear() !== 2025) newErrors.conclusaoEntrega = "A data de conclusão deve ser do ano de 2025";
    }
    if (inicioEntrega && conclusaoEntrega && new Date(inicioEntrega) >= new Date(conclusaoEntrega)) {
      newErrors.conclusaoEntrega = "A data de início deve ser anterior à data de conclusão";
    }

    if (!description.trim()) newErrors.description = "Descrição é obrigatória";
    if (description.length > 500) newErrors.description = "Máximo de 500 caracteres";
    if (!result.trim()) newErrors.result = "Campo obrigatório";
    if (result.length > 2000) newErrors.result = "Máximo de 2000 caracteres";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (saving) return;
    if (!validate()) return;

    setSaving(true);

    try {
      const logEntry: ApprovalLogEntry | null = currentUser
        ? {
            action: "editada",
            user_id: currentUser._id || "",
            user_name: currentUser.detail?.nome_guerra || currentUser.detail?.nome || "",
            user_matricula: currentUser._id || "",
            timestamp: new Date().toISOString(),
          }
        : null;

      const approval_log = logEntry
        ? [...(entrega.approval_log || []), logEntry]
        : entrega.approval_log;

      const entregaAtualizada = {
        ...entrega,
        title,
        description,
        result,
        approval_log,
        updated: new Date().toISOString(),
        start: { $date: new Date(inicioEntrega).toISOString() },
        end: { $date: new Date(conclusaoEntrega).toISOString() },
        indicator: {
          ...entrega.indicator,
          perspectiva: categoria,
          nome: indicadorNome,
        },
      };

      await atualizarEntrega(entregaAtualizada);
      onSave(entregaAtualizada);
    } catch (error) {
      console.error("Erro ao salvar edição:", error);
      alert("Erro ao salvar as alterações. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black bg-opacity-70"
        onClick={onClose}
      />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Editar Iniciativa
          </h2>

          <div className="space-y-4">
            {/* Título */}
            <div>
              <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                Nome da Iniciativa
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={250}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Digite o nome da iniciativa"
              />
              <div className="flex justify-between mt-1">
                {errors.title && <span className="text-red-500 text-xs">{errors.title}</span>}
                <span className="text-right text-xs text-gray-400 ml-auto">{title.length}/250</span>
              </div>
            </div>

            {/* Categoria / Perspectiva ECBB */}
            <div>
              <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                Perspectiva ECBB
              </label>
              <select
                value={categoria}
                onChange={(e) => {
                  setCategoria(e.target.value);
                  setIndicadorNome("");
                }}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Selecione a perspectiva</option>
                {perspectivasECBB.map((p) => (
                  <option key={p._id} value={p.title}>
                    {p.title}
                  </option>
                ))}
              </select>
              {errors.categoria && <span className="text-red-500 text-xs mt-1 block">{errors.categoria}</span>}
            </div>

            {/* Direcionador Estratégico */}
            {perspectivaSelecionada && perspectivaSelecionada.strategicDrivers && (
              <div>
                <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                  Direcionador Estratégico
                </label>
                <select
                  value={indicadorNome}
                  onChange={(e) => setIndicadorNome(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="">Selecione o direcionador</option>
                  {Object.entries(perspectivaSelecionada.strategicDrivers).map(([key, value]) => (
                    <option key={key} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Datas */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                  Data de Início
                </label>
                <input
                  type="date"
                  value={inicioEntrega}
                  onChange={(e) => setInicioEntrega(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                {errors.inicioEntrega && <span className="text-red-500 text-xs mt-1 block">{errors.inicioEntrega}</span>}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                  Data de Conclusão
                </label>
                <input
                  type="date"
                  value={conclusaoEntrega}
                  onChange={(e) => setConclusaoEntrega(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                {errors.conclusaoEntrega && <span className="text-red-500 text-xs mt-1 block">{errors.conclusaoEntrega}</span>}
              </div>
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                Descrição da Iniciativa
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                placeholder="Descreva a iniciativa"
              />
              <div className="flex justify-between mt-1">
                {errors.description && <span className="text-red-500 text-xs">{errors.description}</span>}
                <span className="text-right text-xs text-gray-400 ml-auto">{description.length}/500</span>
              </div>
            </div>

            {/* Resultado */}
            <div>
              <label className="block text-gray-700 font-medium mb-1.5 text-sm">
                Resultado
              </label>
              <textarea
                value={result}
                onChange={(e) => setResult(e.target.value)}
                rows={5}
                maxLength={2000}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                placeholder="Descreva os resultados alcançados"
              />
              <div className="flex justify-between mt-1">
                {errors.result && <span className="text-red-500 text-xs">{errors.result}</span>}
                <span className="text-right text-xs text-gray-400 ml-auto">{result.length}/2000</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors"
            >
              CANCELAR
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 rounded-lg bg-green-bb-2 hover:bg-green-bb text-blue-bb font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "SALVANDO..." : "SALVAR"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
